import { styled } from '@mui/material';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import Box from 'shell/components/Box';
import FileViewerControls from 'shell/components/FileViewerControls';
import Loading from 'shell/components/Loading';
import Popover from 'shell/components/Popover';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

const options = {
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts`,
};

const HIGHLIGHT_CLASS_NAME = 'pdf-highlight';

function stripMarkTag(string) {
    if (string.includes(`<mark class="${HIGHLIGHT_CLASS_NAME}">`) && string.includes('</mark>')) {
        return string.replaceAll(`<mark class="${HIGHLIGHT_CLASS_NAME}">`, '').replaceAll('</mark>', '');
    }
    return string;
}

function getIndexRange(string, substring) {
    const indexStart = Math.max(
        stripMarkTag(string).indexOf(stripMarkTag(substring)),
        stripMarkTag(string).match(stripMarkTag(substring))?.index || -1
    );
    const indexEnd = indexStart + stripMarkTag(substring).length;

    return [indexStart, indexEnd];
}

function getTextItemWithNeighbors(textItems, itemIndex, span = 16) {
    return textItems
        .slice(Math.max(0, itemIndex - span), itemIndex + 1 + span)
        .filter(Boolean)
        .map((item) => item.str)
        .join(' ');
}

const PDFViewer = ({ highlights, isFullScreen, url }) => {
    const documentRef = useRef(null);
    const rootRef = useRef(null);

    const [isLoaded, setIsLoaded] = useState(false);
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [scale, setScale] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const [hidden, setHidden] = useState(false);
    const [textItems, setTextItems] = useState({});
    const [isFocused, setIsFocused] = useState(false);

    const [, renderTrigger] = useState(false);

    const handleClick = (event) => {
        // if (!event.target.classList.contains(HIGHLIGHT_CLASS_NAME)) return;
        // const id = event.target.classList
        //     .values()
        //     .toArray()
        //     .filter((item) => item !== HIGHLIGHT_CLASS_NAME)?.[0];
        // if (!id) {
        //     return console.error(`Highlight ID "${id}" not found.`);
        // }
        // const boundingClientRect = event.target.getBoundingClientRect();
        // setAnchorEl({
        //     getBoundingClientRect: () => {
        //         return boundingClientRect;
        //     },
        //     nodeType: 1,
        // });
    };

    const onScroll = () => {
        let highestDividerNum = 1;
        for (let i = 1; i < numPages; i++) {
            const y = document.getElementById(`divider-${i}`)?.getBoundingClientRect()?.y;
            if (y < rootRef.current.getBoundingClientRect().height / 2) {
                highestDividerNum = i + 1;
            }
        }
        setCurrentPage(highestDividerNum);
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setIsLoaded(true);
        setNumPages(numPages);
    };

    const customTextRenderer = useCallback(
        (textItem) => {
            if (hidden) return textItem.str;

            if (!textItems[textItem.pageIndex]) {
                return textItem.str;
            }

            highlights.forEach(({ value: stringToHighlight }) => {
                const { itemIndex } = textItem;

                const matchInTextItem = textItem.str.match(stringToHighlight);

                if (matchInTextItem) {
                    // Found full match within current item, no need for black magic
                    textItem.str = textItem.str.replaceAll(
                        stringToHighlight,
                        `<mark class="${HIGHLIGHT_CLASS_NAME}">${stringToHighlight}</mark>`
                    );
                } else {
                    // Full match within current item not found, let's check if we can find it
                    // spanned across multiple lines

                    // Get text item with neighbors
                    let textItemWithNeighbors = getTextItemWithNeighbors(textItems[textItem.pageIndex], itemIndex);

                    textItemWithNeighbors = textItemWithNeighbors.replaceAll(' fl ', 'fl');
                    textItemWithNeighbors = textItemWithNeighbors.replaceAll(' fi ', 'fi');
                    textItemWithNeighbors = textItemWithNeighbors.replaceAll('  ', ' ');
                    textItemWithNeighbors = textItemWithNeighbors.replaceAll('- ', '-');

                    const matchInTextItemWithNeighbors =
                        Boolean(textItemWithNeighbors.match(stringToHighlight)) ||
                        textItemWithNeighbors.includes(stringToHighlight);

                    if (!matchInTextItemWithNeighbors) {
                        // No match
                        // do nothing
                    } else {
                        // Now we need to figure out if the match we found was at least partially
                        // in the line we're currently rendering
                        const [matchIndexStart, matchIndexEnd] = getIndexRange(
                            textItemWithNeighbors,
                            stringToHighlight
                        );
                        const [textItemIndexStart, textItemIndexEnd] = getIndexRange(
                            textItemWithNeighbors,
                            textItem.str
                        );

                        if (
                            // Match entirely in the previous line
                            matchIndexEnd < textItemIndexStart ||
                            // Match entirely in the next line
                            matchIndexStart > textItemIndexEnd
                        ) {
                            // do nothing
                        } else {
                            // Match found was partially in the line we're currently rendering. Now
                            // we need to figure out what does "partially" exactly mean

                            // Find partial match in a line
                            const indexOfCurrentTextItemInMergedLines = stripMarkTag(textItemWithNeighbors).indexOf(
                                stripMarkTag(textItem.str)
                            );

                            const matchIndexStartInTextItem = Math.max(
                                0,
                                matchIndexStart - indexOfCurrentTextItemInMergedLines
                            );
                            const matchIndexEndInTextItem = matchIndexEnd - indexOfCurrentTextItemInMergedLines;

                            const partialStringToHighlight = stripMarkTag(textItem.str).slice(
                                matchIndexStartInTextItem,
                                matchIndexEndInTextItem
                            );

                            textItem.str = textItem.str.replaceAll(
                                partialStringToHighlight,
                                `<mark class="${HIGHLIGHT_CLASS_NAME}">${partialStringToHighlight}</mark>`
                            );
                        }
                    }
                }
            });

            return textItem.str;
        },
        [hidden, highlights, textItems]
    );

    const onPageLoadSuccess = useCallback(async (page, pageIndex) => {
        const textContent = await page.getTextContent();
        setTextItems((current) => ({ ...current, [pageIndex]: textContent.items }));
    }, []);

    useEffect(() => {
        setTimeout(() => {
            renderTrigger((current) => !current);
        }, 150);
    }, [isFullScreen]);

    useEffect(() => {
        documentRef.current.setAttribute('tabindex', -1);
    }, []);

    return (
        <RootStack className={'joyride-step-7'} ref={rootRef} onClick={handleClick}>
            <Document
                inputRef={documentRef}
                file={url}
                loading={<Loading />}
                options={options}
                onLoadSuccess={onDocumentLoadSuccess}
                onScroll={onScroll}
            >
                <PagesStack tabIndex={0} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}>
                    {Array.from(Array(numPages)).map((_, index) => (
                        <Fragment key={index}>
                            {Boolean(index) && <PageDivider id={`divider-${index}`} />}
                            <Page
                                customTextRenderer={customTextRenderer}
                                pageNumber={index + 1}
                                scale={1 * scale}
                                width={
                                    documentRef.current
                                        ? Math.floor(documentRef.current.getBoundingClientRect().width) - 1
                                        : undefined
                                }
                                onLoadSuccess={(page) => onPageLoadSuccess(page, index)}
                            />
                        </Fragment>
                    ))}
                </PagesStack>
            </Document>
            {isLoaded && (
                <FileViewerControls
                    currentPage={currentPage}
                    numPages={numPages}
                    scale={scale}
                    setScale={setScale}
                    hidden={hidden}
                    setHidden={setHidden}
                />
            )}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                onClose={() => setAnchorEl(null)}
            >
                <Text>This is a popover.</Text>
            </Popover>
            {isFocused && (
                <Box
                    sx={(theme) => ({
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
                        outlineOffset: '-5px',
                        background: 'transparent',
                    })}
                />
            )}
        </RootStack>
    );
};

const PageDivider = (props) => {
    return (
        <Box
            sx={(theme) => ({ height: theme.spacing(2), background: theme.palette.ensightful.neutral[100] })}
            {...props}
        />
    );
};

const RootStack = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        width: '100%',
        height: '100%',
        letterSpacing: 'normal',
        '& .react-pdf__Document': {
            overflow: 'auto',
        },
        [`& .${HIGHLIGHT_CLASS_NAME}`]: {
            borderBottom: `1px solid ${theme.palette.ensightful.warning[700]}`,
            background: theme.palette.ensightful.warning[300],
            '&:hover': {
                // cursor: 'pointer',
            },
        },
    };
});

const PagesStack = styled(Stack)(() => {
    return {
        '&:focus-visible': {
            outline: 'none',
        },
    };
});

export default PDFViewer;
